// FloorPlanRenameFloorModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

/**
 * FloorPlanRenameFloorModal
 *
 * Renames the active floor via a shared modal + TextInput (not window.prompt).
 *
 * @param {boolean} [props.isOpen=false]
 * @param {string} [props.initialName='']
 * @param {Function} props.onClose
 * @param {Function} props.onSave - (nextName) => void
 * @returns {ReactNode}
 */
const FloorPlanRenameFloorModal = React.memo(({
  isOpen = false,
  initialName = '',
  onClose,
  onSave,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  // Draft floor name
  const [name, setName] = useState('');

  // ----- EFFECTS ----- //

  // Seed the draft when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setName(String(initialName || '').trim());
  }, [initialName, isOpen]);

  // ----- HANDLERS ----- //

  /**
   * handleSave
   *
   * Commits a non-empty trimmed name.
   */
  const handleSave = () => {
    const nextName = String(name || '').trim();
    if (!nextName) return;
    onSave?.(nextName);
  };

  /**
   * handleClose
   */
  const handleClose = () => {
    onClose?.();
  };

  // ----- RENDER ----- //

  if (!isOpen) return null;

  return (
    <Modal
      iconKey="edit"
      isOpen={isOpen}
      title="Rename Floor"
      width={isMobile ? '100%' : 420}
      onClose={handleClose}
    >
      <ModalForm>

        {/* FIELD */}
        <ModalField>
          <ModalLabel label="Floor name" />
          <TextInput
            autoFocus
            autoSelect
            placeholder="Main Floor"
            value={name}
            onChange={(event) => setName(String(event.target.value || ''))}
            onEnterPress={handleSave}
          />
        </ModalField>
      </ModalForm>

      {/* ACTIONS */}
      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            iconKey="close"
            label="Cancel"
            onClick={handleClose}
          />
          <Button
            enabled={Boolean(String(name || '').trim())}
            iconKey="check"
            label="Save"
            onClick={handleSave}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
FloorPlanRenameFloorModal.displayName = 'FloorPlanRenameFloorModal';

export default FloorPlanRenameFloorModal;
